import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { readCachedBrandTheme, type BrandTheme } from './brandTheme';
import { PLATFORM_DEFAULT_THEME } from './platformDefaultTheme';

interface BrandThemeState {
  brandTheme: BrandTheme;
  /** Applies a validated document in place (C5). */
  setBrandTheme: (theme: BrandTheme) => void;
}

const BrandThemeContext = createContext<BrandThemeState | null>(null);

/** Holds the painted brand. A repeat visit paints from the cache on mount, before bootstrap (C4). */
export function BrandThemeProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: BrandTheme;
}) {
  const [brandTheme, setState] = useState<BrandTheme>(
    () => initial ?? readCachedBrandTheme(window.location.hostname) ?? PLATFORM_DEFAULT_THEME,
  );
  const setBrandTheme = useCallback((theme: BrandTheme) => setState(theme), []);
  const value = useMemo(() => ({ brandTheme, setBrandTheme }), [brandTheme, setBrandTheme]);
  return <BrandThemeContext.Provider value={value}>{children}</BrandThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export function useBrandTheme(): BrandThemeState {
  const state = useContext(BrandThemeContext);
  if (!state) throw new Error('useBrandTheme() called outside BrandThemeProvider.');
  return state;
}
